<script setup>
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useAdminCenterStore } from '../../stores/AdminCenterStore'
import { handleErrors } from '../../utils/errorHandler'
import BcsCard from '../common/BcsCard.vue'
import BcsStatusPill from '../common/BcsStatusPill.vue'
import BcsDropdownSelect from '../common/BcsDropdownSelect.vue'
import { getAccountOwners, normalizeOwnerEmail } from '../../utils/accountOwnersHelpers'

const MAX_ACCOUNT_OWNERS = 3

const emit = defineEmits(['showAlert'])

const adminCenterStore = useAdminCenterStore()
const {
  isLoadingEmployers,
  isLoadingMoreAllEmployers,
  isLoadingMoreUnassignedEmployers,
  allEmployers,
  unassignedEmployersList,
  totalAllEmployers,
  totalUnassignedEmployers,
  hasMoreAllEmployers,
  hasMoreUnassignedEmployers,
  allActiveUsers
} = storeToRefs(adminCenterStore)

const activeTab = ref('all')
const isSubmitting = ref(false)

// Track changes by employerId (not planYearId), since account owners live
// on the employer document. This ensures all plan year rows for the same
// employer stay in sync when the user changes the owners on any one of them.
const ownerChanges = ref({}) // { [employerId]: string[] }

const hasChanges = computed(() => Object.keys(ownerChanges.value).length > 0)

// Build owner options from active users for the dropdown.
// No 'Unassigned' sentinel in multi-select mode: an empty selection means unassigned.
const ownerOptions = computed(() =>
  allActiveUsers.value
    .map(u => ({ value: normalizeOwnerEmail(u.email), label: `${u.fullname}` }))
    .filter(o => o.value)
)

function formatDate(value) {
  if (!value) return 'N/a'
  const date = new Date(value)
  if (isNaN(date.getTime())) return 'N/a'
  return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
}

function getSelectedOwners(employer) {
  if (ownerChanges.value[employer.employerId] !== undefined) {
    return ownerChanges.value[employer.employerId]
  }
  return getAccountOwners(employer)
}

const sameOwners = (a, b) => [...a].sort().join('|') === [...b].sort().join('|')

function onOwnersChange(employer, newOwners) {
  const owners = Array.isArray(newOwners) ? newOwners : []
  // If reverting to the original set, remove from changes
  if (sameOwners(owners, getAccountOwners(employer))) {
    const copy = { ...ownerChanges.value }
    delete copy[employer.employerId]
    ownerChanges.value = copy
  } else {
    ownerChanges.value = { ...ownerChanges.value, [employer.employerId]: owners }
  }
}

async function onRefresh() {
  ownerChanges.value = {}
  await adminCenterStore.refreshEmployers()
}

async function submitAssignments() {
  if (!hasChanges.value || isSubmitting.value) return
  isSubmitting.value = true

  try {
    const assignments = Object.entries(ownerChanges.value).map(([employerId, accountOwners]) => ({
      employerId,
      accountOwners
    }))

    if (assignments.length === 0) return

    await adminCenterStore.submitAssignments(assignments)
    ownerChanges.value = {}

    emit('showAlert', `Successfully updated ${assignments.length} employer assignment${assignments.length !== 1 ? 's' : ''}!`)
  } catch (error) {
    handleErrors('EmployerAssignments.submitAssignments', error)
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <BcsCard title="Employer Assignments">
    <template #header-right>
      <button
        class="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
        type="button"
        :disabled="isLoadingEmployers"
        @click="onRefresh"
      >
        <i class="bi bi-arrow-clockwise d-flex py-1" :class="{ 'spin-animation': isLoadingEmployers }"></i>
      </button>
    </template>
    <div class="px-3 pt-4">
      <!-- Tabs -->
      <ul class="nav nav-tabs mb-0 border-0">
        <li class="nav-item">
          <button
            class="nav-link employer-tab text-16"
            :class="{ active: activeTab === 'all' }"
            @click="activeTab = 'all'"
            type="button"
          >
            All Employers ({{ totalAllEmployers }})
          </button>
        </li>
        <li class="nav-item">
          <button
            class="nav-link employer-tab text-16"
            :class="{ active: activeTab === 'unassigned' }"
            @click="activeTab = 'unassigned'"
            type="button"
          >
            Unassigned Employers ({{ totalUnassignedEmployers }})
          </button>
        </li>
      </ul>
    </div>

    <!-- All Employers Tab -->
    <div v-if="activeTab === 'all'">
      <div v-if="isLoadingEmployers" class="text-center py-4">
        <div class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <table v-else class="table employer-table align-middle mb-0">
        <colgroup>
          <col style="width: 18%" />
          <col style="width: 20%" />
          <col style="width: 16%" />
          <col style="width: 22%" />
          <col style="width: 24%" />
        </colgroup>
        <thead>
          <tr class="text-16">
            <th class="fw-bold py-3 ps-4">Status</th>
            <th class="fw-bold py-3">Employer</th>
            <th class="fw-bold py-3">Plan Year Start</th>
            <th class="fw-bold py-3">Medical Plan Funding</th>
            <th class="fw-bold py-3">Reassign Owner</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="employer in allEmployers" :key="employer.planYearId" class="text-14">
            <td class="ps-4">
              <BcsStatusPill
                :text="employer.isAssigned ? 'Assigned' : 'Unassigned'"
                :color="employer.isAssigned ? 'success' : 'danger'"
                :bold="false"
              />
            </td>
            <td>{{ employer.name }}</td>
            <td>{{ formatDate(employer.planStartDate) }}</td>
            <td>{{ employer.medicalPlanFunding || '--' }}</td>
            <td class="pe-4">
              <BcsDropdownSelect
                :modelValue="getSelectedOwners(employer)"
                @update:modelValue="onOwnersChange(employer, $event)"
                :options="ownerOptions"
                :multiSelect="true"
                :maxSelections="MAX_ACCOUNT_OWNERS"
                placeholder="Unassigned"
                variant="primary"
              />
            </td>
          </tr>
          <tr v-if="allEmployers.length === 0 && !isLoadingEmployers">
            <td colspan="5" class="text-center text-muted py-3">No employers found.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Unassigned Employers Tab -->
    <div v-else-if="activeTab === 'unassigned'">
      <div v-if="isLoadingEmployers" class="text-center py-4">
        <div class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <table v-else class="table employer-table align-middle mb-0">
        <colgroup>
          <col style="width: 18%" />
          <col style="width: 20%" />
          <col style="width: 16%" />
          <col style="width: 22%" />
          <col style="width: 24%" />
        </colgroup>
        <thead>
          <tr class="text-16">
            <th class="fw-bold py-3 ps-4">Status</th>
            <th class="fw-bold py-3">Employer</th>
            <th class="fw-bold py-3">Plan Year Start</th>
            <th class="fw-bold py-3">Medical Plan Funding</th>
            <th class="fw-bold py-3">Reassign Owner</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="employer in unassignedEmployersList" :key="employer.planYearId" class="text-14">
            <td class="ps-4">
              <BcsStatusPill text="Unassigned" color="danger" :bold="false"/>
            </td>
            <td>{{ employer.name }}</td>
            <td>{{ formatDate(employer.planStartDate) }}</td>
            <td>{{ employer.medicalPlanFunding || '--' }}</td>
            <td class="pe-4">
              <BcsDropdownSelect
                :modelValue="getSelectedOwners(employer)"
                @update:modelValue="onOwnersChange(employer, $event)"
                :options="ownerOptions"
                :multiSelect="true"
                :maxSelections="MAX_ACCOUNT_OWNERS"
                placeholder="Unassigned"
                variant="primary"
              />
            </td>
          </tr>
          <tr v-if="unassignedEmployersList.length === 0 && !isLoadingEmployers">
            <td colspan="5" class="text-center text-muted py-3">No unassigned employers found.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </BcsCard>

  <!-- Load More & Submit (outside card) -->
  <div v-if="activeTab === 'all' && !isLoadingEmployers && allEmployers.length > 0" class="d-flex justify-content-between align-items-start mt-3">
    <div>
      <p class="text-14 mb-0" style="color: var(--bs-gray-700)">
        You're viewing {{ allEmployers.length > totalAllEmployers ? totalAllEmployers : allEmployers.length }} of {{ totalAllEmployers }} employers
      </p>
      <button
        v-if="hasMoreAllEmployers"
        type="button"
        class="btn btn-info text-16 mt-2"
        :disabled="isLoadingMoreAllEmployers"
        @click="adminCenterStore.loadMoreAllEmployers()"
      >
        <med>Load More</med>
        <i v-if="!isLoadingMoreAllEmployers" class="bi bi-chevron-double-down ms-2"></i>
        <span v-if="isLoadingMoreAllEmployers" class="spinner-border spinner-border-sm ms-2" role="status"></span>
      </button>
    </div>
    <button
      type="button"
      class="btn btn-primary px-4"
      :disabled="!hasChanges || isSubmitting"
      @click="submitAssignments"
    >
      Submit Assignments
      <span v-if="isSubmitting" class="spinner-border spinner-border-sm ms-2" role="status"></span>
    </button>
  </div>
  <div v-if="activeTab === 'unassigned' && !isLoadingEmployers && unassignedEmployersList.length > 0" class="d-flex justify-content-between align-items-start mt-3">
    <div>
      <p class="text-14 mb-0" style="color: var(--bs-gray-700)">
        You're viewing {{ unassignedEmployersList.length > totalUnassignedEmployers ? totalUnassignedEmployers : unassignedEmployersList.length }} of {{ totalUnassignedEmployers }} unassigned employers
      </p>
      <button
        v-if="hasMoreUnassignedEmployers"
        type="button"
        class="btn btn-info text-16 mt-2"
        :disabled="isLoadingMoreUnassignedEmployers"
        @click="adminCenterStore.loadMoreUnassignedEmployers()"
      >
        <med>Load More</med>
        <i v-if="!isLoadingMoreUnassignedEmployers" class="bi bi-chevron-double-down ms-2"></i>
        <span v-if="isLoadingMoreUnassignedEmployers" class="spinner-border spinner-border-sm ms-2" role="status"></span>
      </button>
    </div>
    <button
      type="button"
      class="btn btn-primary px-4"
      :disabled="!hasChanges || isSubmitting"
      @click="submitAssignments"
    >
      Submit Assignments
      <span v-if="isSubmitting" class="spinner-border spinner-border-sm ms-2" role="status"></span>
    </button>
  </div>
</template>

<style scoped lang="scss">
:deep(.card-body) {
  padding: 0;
}

.employer-tab {
  color: var(--bs-primary) !important;
  font-weight: 500 !important;
  border: 1px solid var(--bs-border-color);
  border-bottom: none;
  background-color: transparent;

  &.active {
    color: var(--bs-secondary) !important;
    background-color: var(--bs-white);
    border-bottom-color: var(--bs-white);
  }

  &:hover:not(.active) {
    color: var(--bs-primary);
  }
}

.employer-table {
  border-collapse: collapse;

  th, td {
    border-left: none;
    border-right: none;
  }

  thead th {
    background-color: var(--bs-white);
    border-bottom: 2px solid var(--bs-border-color);
    border-top: 1px solid var(--bs-border-color);
  }

  tbody td {
    border-bottom: 1px solid var(--bs-border-color);
  }

  tbody tr:last-child td {
    border-bottom: 1px solid var(--bs-border-color);
  }
}

.spin-animation {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
